/**
 * /tools/legacy-letter?clientId=X — STP-045 (Dual Letters).
 *
 * Lists the client's prior letters (filtered by the sealed-letter
 * invariant — sealed rows whose seal_date hasn't passed are NOT returned
 * by the backend) + a form to write a new letter.
 *
 * Sealed-letter invariant (CLAUDE.md §7.8): the backend filters; never
 * trust the client to enforce it. The web surface only renders what the
 * server returns.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/app/auth-context';
import * as api from '@/lib/api-client';
import { useClientId } from '@/lib/use-client-id';
import { ApiError } from '@/lib/api-client';
import { FinalizeWarning } from '@/components/finalize-warning';
import { PrintButton } from '@/components/print-button';

type LetterType = 'from_future_self' | 'to_future_self';

interface FormState {
  letterType: LetterType;
  letterText: string;
  signedName: string;
  writtenDate: string;
  isSealed: boolean;
  sealDate: string;
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

const DEFAULT_FORM: FormState = {
  letterType: 'from_future_self',
  letterText: '',
  signedName: '',
  writtenDate: todayISO(),
  isSealed: false,
  sealDate: '',
};

export function LegacyLetterPage(): JSX.Element {
  const { t } = useTranslation();
  const { accessToken } = useAuth();
  const clientId = useClientId();

  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [letters, setLetters] = useState<api.LegacyLetter[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  const refresh = async (token: string, id: string): Promise<void> => {
    const list = await api.listLegacyLetters(token, id);
    setLetters(list.data);
  };

  useEffect(() => {
    let active = true;
    if (!clientId) {
      setLoading(false);
      return;
    }
    void (async () => {
      try {
        await refresh(accessToken, clientId);
      } catch {
        /* swallow — empty list is fine */
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken, clientId]);

  const canSubmit =
    !submitting &&
    form.letterText.trim().length > 0 &&
    form.writtenDate.length > 0 &&
    (!form.isSealed || form.sealDate.length > 0);

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setSubmitError(null);
    if (!clientId) {
      setSubmitError(t('tools.legacyLetter.errors.missingClient'));
      return;
    }
    setSubmitting(true);
    try {
      await api.createLegacyLetter(accessToken, clientId, {
        letter_type: form.letterType,
        letter_text: form.letterText.trim(),
        ...(form.signedName.trim() ? { signed_name: form.signedName.trim() } : {}),
        written_date: form.writtenDate,
        ...(form.isSealed && form.sealDate ? { seal_date: form.sealDate } : {}),
        is_sealed: form.isSealed,
      });
      await refresh(accessToken, clientId);
      setSavedAt(new Date().toISOString());
      setForm({ ...DEFAULT_FORM, writtenDate: todayISO() });
    } catch (cause) {
      if (cause instanceof ApiError) {
        setSubmitError(cause.envelope.message || t('tools.legacyLetter.errors.generic'));
      } else {
        setSubmitError(t('tools.legacyLetter.errors.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!clientId) {
    return (
      <section className="max-w-2xl mx-auto px-s5 py-s8" data-testid="legacy-letter-missing-client">
        <h1 className="font-display text-display-lg text-ink">{t('tools.legacyLetter.title')}</h1>
        <p className="mt-s3 text-flame-ember">{t('tools.legacyLetter.errors.missingClient')}</p>
      </section>
    );
  }

  return (
    <section className="max-w-3xl mx-auto px-s5 py-s7">
      <div className="flex justify-end mb-s2">
        <PrintButton />
      </div>
      <header className="mb-s6">
        <p className="font-mono text-xs uppercase tracking-wide text-gray-1">
          {t('tools.legacyLetter.eyebrow')}
        </p>
        <h1 className="font-display text-display-xl text-ink mt-s2">
          {t('tools.legacyLetter.title')}
        </h1>
        <p className="mt-s3 text-gray-2 max-w-prose">{t('tools.legacyLetter.description')}</p>
      </header>

      {!loading && letters.length > 0 && (
        <article className="mb-s5 rounded-lg border border-bone-3 bg-bone-2 p-s5">
          <h2 className="font-display text-display-sm text-ink">
            {t('tools.legacyLetter.priorLetters.title')}
          </h2>
          <ul className="mt-s3 space-y-s3" data-testid="legacy-letter-prior">
            {letters.map((letter) => (
              <li
                key={letter.id}
                className="rounded-md border border-bone-3 bg-bone p-s3"
                data-testid={`legacy-letter-row-${letter.id}`}
              >
                <p className="font-mono text-xs uppercase tracking-wide text-gray-1">
                  {t(`tools.legacyLetter.types.${letter.letter_type}`)} ·{' '}
                  {letter.written_date}
                </p>
                <p className="mt-s2 text-ink whitespace-pre-wrap">{letter.letter_text}</p>
                {letter.signed_name && (
                  <p className="mt-s2 text-flame-deep italic">— {letter.signed_name}</p>
                )}
              </li>
            ))}
          </ul>
        </article>
      )}

      <form
        onSubmit={(event) => {
          void onSubmit(event);
        }}
        className="space-y-s5"
        noValidate
      >
        <article className="rounded-lg border border-bone-3 bg-bone-2 p-s5 space-y-s3">
          <div role="radiogroup" aria-label={t('tools.legacyLetter.fields.letterType')}>
            <p className="form-label">{t('tools.legacyLetter.fields.letterType')}</p>
            <div className="flex gap-s3 mt-s2">
              {(['from_future_self', 'to_future_self'] as LetterType[]).map((lt) => (
                <button
                  key={lt}
                  type="button"
                  role="radio"
                  aria-checked={form.letterType === lt}
                  onClick={() => setForm((prev) => ({ ...prev, letterType: lt }))}
                  data-testid={`legacy-letter-type-${lt}`}
                  className={
                    form.letterType === lt
                      ? 'px-s4 py-s2 rounded-md bg-flame text-bone font-medium'
                      : 'px-s4 py-s2 rounded-md border border-bone-3 text-gray-2 hover:text-ink'
                  }
                >
                  {t(`tools.legacyLetter.types.${lt}`)}
                </button>
              ))}
            </div>
          </div>
          <label className="block">
            <span className="form-label">{t('tools.legacyLetter.fields.writtenDate')}</span>
            <input
              type="date"
              value={form.writtenDate}
              onChange={(e) => setForm((prev) => ({ ...prev, writtenDate: e.target.value }))}
              className="input-field mt-s2"
              data-testid="legacy-letter-written-date"
            />
          </label>
          <label className="block">
            <span className="form-label">
              {t('tools.legacyLetter.fields.signedName')}
              <span className="ml-s2 text-xs font-normal uppercase tracking-wide text-gray-1">
                {t('tools.legacyLetter.optional')}
              </span>
            </span>
            <input
              type="text"
              value={form.signedName}
              onChange={(e) => setForm((prev) => ({ ...prev, signedName: e.target.value }))}
              placeholder={t('tools.legacyLetter.placeholders.signedName')}
              className="input-field mt-s2 w-full"
              data-testid="legacy-letter-signed-name"
            />
          </label>
        </article>

        <article className="rounded-lg border border-bone-3 bg-bone-2 p-s5">
          <label className="block">
            <span className="form-label">{t('tools.legacyLetter.fields.letterText')}</span>
            <p className="text-xs text-gray-1 mb-s2 italic">
              {t('tools.legacyLetter.hints.letterText')}
            </p>
            <textarea
              value={form.letterText}
              onChange={(e) => setForm((prev) => ({ ...prev, letterText: e.target.value }))}
              placeholder={t('tools.legacyLetter.placeholders.letterText')}
              className="input-field mt-s2 min-h-64"
              data-testid="legacy-letter-text"
              rows={12}
            />
          </label>
        </article>

        <article className="rounded-lg border border-flame-ember bg-bone-2 p-s5">
          <label className="block">
            <input
              type="checkbox"
              checked={form.isSealed}
              onChange={(e) => setForm((prev) => ({ ...prev, isSealed: e.target.checked }))}
              data-testid="legacy-letter-is-sealed"
              className="mr-s2"
            />
            <span className="form-label inline">{t('tools.legacyLetter.fields.isSealed')}</span>
            <p className="text-xs text-gray-1 mt-s1 italic">
              {t('tools.legacyLetter.hints.isSealed')}
            </p>
          </label>
          {form.isSealed && (
            <label className="block mt-s3">
              <span className="form-label">{t('tools.legacyLetter.fields.sealDate')}</span>
              <input
                type="date"
                value={form.sealDate}
                onChange={(e) => setForm((prev) => ({ ...prev, sealDate: e.target.value }))}
                className="input-field mt-s2"
                data-testid="legacy-letter-seal-date"
              />
            </label>
          )}
        </article>

        {submitError && (
          <p role="alert" className="form-error" data-testid="legacy-letter-submit-error">
            {submitError}
          </p>
        )}

        {savedAt && (
          <p role="status" className="text-sm text-flame-deep" data-testid="legacy-letter-saved-at">
            ✓ {t('tools.legacyLetter.savedAt', { when: savedAt.slice(0, 19).replace('T', ' ') })}
          </p>
        )}

        <button type="submit" className="btn-primary w-full" disabled={!canSubmit}>
          {submitting ? t('tools.legacyLetter.submitting') : t('tools.legacyLetter.submit')}
        </button>
        <FinalizeWarning />
      </form>
    </section>
  );
}
